import React, { useRef } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import Animated, {
    Extrapolate,
    interpolate,
    useAnimatedStyle,
} from 'react-native-reanimated';

import { BottomSheetHandleProps } from '@gorhom/bottom-sheet';

import CloseIcon from 'assets/images/svgs/ico_close.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { BOTTOM_SHEET_CLOSE_BUTTON_ID } from 'config/testID';

import useHeaderHeight from 'utils/hooks/useHeaderHeight';

import SvgButton from 'components/CustomButtons/SvgButton';
import useAccessibilityFocus from 'components/Gallery/hooks/useAccessibilityFocus';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    animatedIndex?: BottomSheetHandleProps['animatedIndex'];
    title: string;
    onClosePress(): void;
};

const { CLOSE_BUTTON } = LANGUAGE_KEYS;

function BottomSheetHandle({ title, onClosePress, animatedIndex }: Props) {
    const { t } = useTranslation();
    const height = useHeaderHeight();

    const titleRef = useRef(null);

    const animatedStyle = useAnimatedStyle(
        () =>
            animatedIndex
                ? {
                      opacity: interpolate(
                          animatedIndex.value,
                          [0, 0.9, 1],
                          [0, 0, 1],
                          Extrapolate.CLAMP,
                      ),
                      height: interpolate(
                          animatedIndex.value,
                          [0, 0.7, 1],
                          [0, 0, height],
                          Extrapolate.CLAMP,
                      ),
                  }
                : {},
        [height],
    );

    useAccessibilityFocus({ accessibleViewRef: titleRef, focused: !!title });

    return (
        <Animated.View style={[styles.root, { height }, animatedStyle]}>
            <View style={styles.header}>
                <Text
                    accessibilityRole="header"
                    color={ETextColor.high_contrast}
                    category={ETextCategory.title3}
                    ref={titleRef}
                >
                    {title}
                </Text>
                <SvgButton
                    accessible
                    accessibilityLabel={t(CLOSE_BUTTON)}
                    testID={BOTTOM_SHEET_CLOSE_BUTTON_ID}
                    Svg={CloseIcon}
                    onPress={onClosePress}
                />
            </View>
        </Animated.View>
    );
}

export default BottomSheetHandle;
